import { useEffect, useState } from 'react'
import { collection, collectionGroup, getDocs, onSnapshot, query, where } from 'firebase/firestore'
import type { CarSummaryDoc } from '@racedash/shared'
import { db } from '../lib/firebase'
import { useReplayOf } from './replayContext'

/**
 * Every car's bests for one session.
 *
 * Sixty small documents, which is what makes "best in class" a live figure
 * rather than a scan of every lap of every car. Listened to rather than fetched
 * because a session best moves during a session and a stale one is worse than
 * none — somebody would act on it.
 */
export function useSessionSummaries(sessionId: string | null): CarSummaryDoc[] {
  const [rows, setRows] = useState<CarSummaryDoc[]>([])
  // During a replay, the bests as they stood then.
  const replay = useReplayOf(sessionId)
  const replaying = replay != null

  useEffect(() => {
    if (!sessionId || replaying) {
      setRows([])
      return
    }
    const ref = collection(db, 'streamSessions', sessionId, 'carSummaries')
    return onSnapshot(
      ref,
      (snap) => setRows(snap.docs.map((d) => d.data() as CarSummaryDoc)),
      // Swallowed like every other timing listener: an unreadable collection
      // reads as "no comparison available", which is what the cards already
      // show when there is nothing to compare to.
      () => setRows([]),
    )
  }, [sessionId, replaying])

  return replay?.frame.carSummaries ?? rows
}

/**
 * One car's bests across every session of an event.
 *
 * A collection-group query, which is the whole reason eventId is denormalised
 * onto these documents: the alternative is finding the event's sessions and
 * then reading each one, and the number of sessions in a weekend is not known
 * until you have asked.
 *
 * A one-shot read rather than a listener. This answers "did we go quicker in
 * practice", which is a question about the past — and subscribing to every
 * session of a weekend to watch a number that will not change is a listener per
 * session for nothing.
 */
export function useEventSummaries(eventId: string | null, car: string | null): CarSummaryDoc[] {
  const [rows, setRows] = useState<CarSummaryDoc[]>([])

  useEffect(() => {
    if (!eventId || !car) {
      setRows([])
      return
    }
    let live = true
    const q = query(
      collectionGroup(db, 'carSummaries'),
      where('eventId', '==', eventId),
      where('car', '==', car),
    )
    getDocs(q)
      .then((snap) => {
        if (live) setRows(snap.docs.map((d) => d.data() as CarSummaryDoc))
      })
      .catch(() => {
        if (live) setRows([])
      })
    // The flag rather than an unsubscribe, because there is no subscription —
    // it only stops a late answer setting state on an unmounted screen.
    return () => {
      live = false
    }
  }, [eventId, car])

  return rows
}
